import type { OptionsVisibility } from '@atlassian/ui-modifications-core/types';
import { FieldValueLookupError } from '@atlassian/ui-modifications-core/utils';
import type {
	MultiSelectPublicSetValue,
	PublicGetValue,
} from '@atlassian/jira-ui-modifications-common/src/common/utils/common/fields/multi-select.tsx';
import { applyOptionsVisibilityRule } from '@atlassian/jira-ui-modifications-common/src/common/utils/common/utils/apply-options-visibility-rule/index.tsx';
import { getAvailableOptionsVisibilityRule } from '@atlassian/jira-ui-modifications-common/src/common/utils/common/utils/get-available-options-visibility-rule/index.tsx';
import { getValueLookupValuesDiff } from '@atlassian/jira-ui-modifications-common/src/common/utils/common/utils/get-value-lookup-values-diff/index.tsx';
import { isNotEmptyString } from '@atlassian/ui-modifications-public-api-utils/is-not-empty-string';
import { multiValueLookup } from '@atlassian/jira-ui-modifications-common/src/common/utils/common/utils/multi-value-lookup/index.tsx';

export type InternalValue = {
	label: string;
	value: string;
};

export type MultiSelectLookupValue = {
	id: string;
	value: string;
};

export type MultiSelectPublicGetValue = PublicGetValue[];

export type MultiSelectInternalValue = InternalValue[];

export const transformMultiSelectSetOptionsVisibility = (
	optionsVisibility: OptionsVisibility,
	lookupValues: MultiSelectPublicGetValue,
): MultiSelectLookupValue[] => applyOptionsVisibilityRule(optionsVisibility, lookupValues, 'id');

export const transformMultiSelectGetOptionsVisibility = (
	optionsVisibility: OptionsVisibility,
	lookupValues: MultiSelectPublicGetValue,
): OptionsVisibility => getAvailableOptionsVisibilityRule(optionsVisibility, lookupValues, 'id');

export const validateMultiSelectSetValue = (value: unknown): value is MultiSelectPublicSetValue =>
	Array.isArray(value) && value.every(isNotEmptyString);

export const multiSelectSetValueTransformer = (
	values: MultiSelectPublicSetValue,
	lookupValues?: MultiSelectLookupValue[],
): MultiSelectInternalValue => {
	if (!values.length) return [];

	const filteredValues = multiValueLookup<PublicGetValue>(values, lookupValues, 'id');

	if (filteredValues.length !== values.length) {
		const notFoundValues = getValueLookupValuesDiff(values, filteredValues, 'id');

		throw new FieldValueLookupError(notFoundValues.join());
	}

	return filteredValues.map((val) => ({ label: val.value, value: val.id }));
};

export const multiSelectGetValueTransformer = (
	internalValue: MultiSelectInternalValue,
): MultiSelectPublicGetValue => {
	if (!internalValue) return [];

	return internalValue.map(({ value, label }) => ({
		id: value,
		value: label,
	}));
};
